/**
 * Leaflet Voronoi / contour map: local-km data projected via contour origin,
 * same basemap stack as other field maps, pan/zoom with overzoom for small areas.
 */
import { Box, Typography } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polygon,
  Polyline,
  useMap,
  ZoomControl,
} from 'react-leaflet';

import { MapBaseLayers } from '@/components/Map/mapLayers';
import type { MbGridYear, MbPoint } from '@/types/materialBalance';
import {
  localKmToLatLng,
  type GeoOrigin,
} from '@/utils/localKmProjection';

export type VoronoiMapWell = {
  id: string;
  x: number;
  y: number;
  kind: 'prod' | 'inj';
  /** Optional online year shown next to the well name */
  t0?: number;
};

type WellYearState = { id: string; so?: number };
type LatLngTuple = [number, number];

function soColor(so: number, so0: number): string {
  const t = Math.max(0, Math.min(1, so / Math.max(so0, 0.05)));
  const r = Math.round(40 + (180 - 40) * (1 - t));
  const g = Math.round(50 + (200 - 50) * t);
  const b = Math.round(70 + (90 - 70) * (1 - t));
  return `rgba(${r},${g},${b},0.55)`;
}

function toLatLng(pt: { x: number; y: number }, origin: GeoOrigin): LatLngTuple {
  const g = localKmToLatLng(pt.x, pt.y, origin);
  return [g.lat, g.lng];
}

function wellLabelIcon(label: string, kind: 'prod' | 'inj') {
  const color = kind === 'inj' ? '#2563eb' : '#16a34a';
  return L.divIcon({
    className: 'voronoi-mb-well-label',
    html: `<span style="
      display:inline-block;
      padding:1px 5px;
      border-radius:3px;
      background:rgba(255,255,255,0.92);
      color:#0f172a;
      border:1px solid ${color};
      font:600 10px/1.25 system-ui,sans-serif;
      white-space:nowrap;
      box-shadow:0 1px 2px rgba(0,0,0,0.18);
      pointer-events:none;
      transform:translate(8px,-18px);
    ">${label.replace(/</g, '&lt;')}</span>`,
    iconSize: [1, 1],
    iconAnchor: [0, 0],
  });
}

export function FitDataBounds({
  positions,
  fitKey,
}: {
  positions: LatLngTuple[];
  /** Change only when the spatial extent should re-fit (not year scrub). */
  fitKey: string;
}) {
  const map = useMap();
  const lastKey = useRef('');

  useEffect(() => {
    if (positions.length < 1) return;
    if (lastKey.current === fitKey) return;
    lastKey.current = fitKey;
    const bounds = L.latLngBounds(positions);
    if (!bounds.isValid()) return;
    map.fitBounds(bounds.pad(0.18), { animate: false, maxZoom: 19 });
  }, [map, positions, fitKey]);

  return null;
}

export function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const t = window.setTimeout(() => map.invalidateSize(), 80);
    const t2 = window.setTimeout(() => map.invalidateSize(), 320);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [map]);
  return null;
}

export interface VoronoiMbMapProps {
  contour: MbPoint[];
  wells: VoronoiMapWell[];
  grid: MbGridYear | null | undefined;
  wellYear?: WellYearState[];
  so0?: number;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  /** When set, only draw LOS links for this injector */
  highlightInj?: string | null;
  height?: number | string;
  /** Geographic origin for local-km ↔ lat/lng (required for basemap) */
  origin?: GeoOrigin | null;
  /**
   * When set, re-fit bounds only when this key changes (e.g. placement recalc).
   * Use this when wells are filtered by year scrub so the camera stays put.
   */
  fitKey?: string;
}

export const VoronoiMbMap: React.FC<VoronoiMbMapProps> = ({
  contour,
  wells,
  grid,
  wellYear = [],
  so0 = 0.5,
  selectedId = null,
  onSelect,
  highlightInj = null,
  height = '100%',
  origin = null,
  fitKey: fitKeyProp,
}) => {
  const geoOrigin = useMemo<GeoOrigin>(() => {
    if (origin && Number.isFinite(origin.lat) && Number.isFinite(origin.lng)) {
      return origin;
    }
    return { lat: 60, lng: 5 };
  }, [origin]);

  const contourLL = useMemo(
    () => (contour || []).map((p) => toLatLng(p, geoOrigin)),
    [contour, geoOrigin],
  );

  const wellsLL = useMemo(
    () =>
      (wells || []).map((w) => ({
        ...w,
        ll: toLatLng(w, geoOrigin),
        label: w.t0 != null ? `${w.id} · ${w.t0}` : w.id,
      })),
    [wells, geoOrigin],
  );

  const byId = useMemo(
    () => Object.fromEntries((wellYear || []).map((w) => [w.id, w])),
    [wellYear],
  );

  const prodCells = useMemo(
    () =>
      (grid?.prod || []).map((cell) => ({
        id: cell.id,
        positions: (cell.poly || []).map((p) => toLatLng(p, geoOrigin)),
        fill: soColor(byId[cell.id]?.so ?? so0, so0),
      })),
    [grid?.prod, geoOrigin, byId, so0],
  );

  const injCells = useMemo(
    () =>
      (grid?.inj || []).map((cell) => ({
        id: cell.id,
        positions: (cell.poly || []).map((p) => toLatLng(p, geoOrigin)),
      })),
    [grid?.inj, geoOrigin],
  );

  const links = useMemo(() => {
    const raw = grid?.links || [];
    return raw
      .filter((l) => {
        if (highlightInj) return l.inj === highlightInj && !l.blocked && l.w > 0;
        return !l.blocked && l.w > 0;
      })
      .map((l, i) => {
        const a = wells.find((w) => w.id === l.inj);
        const b = wells.find((w) => w.id === l.prod);
        if (!a || !b) return null;
        const overlap = l.overlap;
        const mid =
          overlap && overlap.length
            ? overlap.reduce(
                (s, p) => ({
                  x: s.x + p.x / overlap.length,
                  y: s.y + p.y / overlap.length,
                }),
                { x: 0, y: 0 },
              )
            : { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        return {
          key: `link-${i}`,
          from: toLatLng(a, geoOrigin),
          to: toLatLng(mid, geoOrigin),
          hi: Boolean(highlightInj && l.inj === highlightInj),
          w: l.w,
        };
      })
      .filter(Boolean) as {
      key: string;
      from: LatLngTuple;
      to: LatLngTuple;
      hi: boolean;
      w: number;
    }[];
  }, [grid?.links, wells, geoOrigin, highlightInj]);

  const fitPositions = useMemo(() => {
    const pts: LatLngTuple[] = [...contourLL];
    for (const w of wellsLL) pts.push(w.ll);
    return pts;
  }, [contourLL, wellsLL]);

  // Re-fit when contour / origin changes — not when year scrub filters wells
  const fitKey = useMemo(() => {
    if (fitKeyProp != null) return fitKeyProp;
    const c = contour || [];
    const w = wells || [];
    return `${geoOrigin.lat.toFixed(5)},${geoOrigin.lng.toFixed(5)}|${c.length}|${
      c[0]?.x ?? ''
    },${c[0]?.y ?? ''}|${w.length}|${w[0]?.x ?? ''},${w[0]?.y ?? ''}`;
  }, [contour, wells, geoOrigin, fitKeyProp]);

  const empty = contourLL.length === 0 && wellsLL.length === 0;
  const center: LatLngTuple = contourLL[0] || wellsLL[0]?.ll || [geoOrigin.lat, geoOrigin.lng];

  return (
    <Box
      sx={{
        width: '100%',
        height,
        minHeight: 240,
        borderRadius: 1,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        position: 'relative',
        boxSizing: 'border-box',
        '& .leaflet-container': {
          height: '100%',
          width: '100%',
          zIndex: 0,
          background: '#e8eef5',
        },
        '& .voronoi-mb-well-label': {
          background: 'transparent',
          border: 'none',
        },
      }}
    >
      {!empty ? (
        <MapContainer
          center={center}
          zoom={14}
          minZoom={2}
          maxZoom={22}
          scrollWheelZoom
          zoomControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          <MapBaseLayers maxZoom={22} />
          <ZoomControl position="bottomright" />
          <InvalidateOnResize />
          <FitDataBounds positions={fitPositions} fitKey={fitKey} />

          {prodCells.map((cell) =>
            cell.positions.length >= 3 ? (
              <Polygon
                key={`p-${cell.id}`}
                positions={cell.positions}
                pathOptions={{
                  fillColor: cell.fill,
                  fillOpacity: 1,
                  color: '#64748b',
                  weight: 1,
                }}
              />
            ) : null,
          )}
          {injCells.map((cell) =>
            cell.positions.length >= 3 ? (
              <Polygon
                key={`i-${cell.id}`}
                positions={cell.positions}
                pathOptions={{
                  fill: false,
                  color: '#4aa3ff',
                  weight: 1.5,
                  dashArray: '4 3',
                  opacity: 0.7,
                }}
              />
            ) : null,
          )}

          {contourLL.length >= 2 && (
            <Polygon
              positions={contourLL}
              pathOptions={{
                fillOpacity: 0,
                color: '#0f172a',
                weight: 2.5,
                opacity: 0.9,
              }}
            />
          )}

          {links.map((l) => (
            <Polyline
              key={l.key}
              positions={[l.from, l.to]}
              pathOptions={{
                color: l.hi ? '#2563eb' : '#60a5fa',
                weight: l.hi ? 2 + 5 * l.w : 1 + 3 * l.w,
                opacity: 0.9,
              }}
            />
          ))}

          {wellsLL.map((w) => {
            const selected = selectedId === w.id || highlightInj === w.id;
            return (
              <React.Fragment key={w.id}>
                <CircleMarker
                  center={w.ll}
                  radius={selected ? 9 : 7}
                  pathOptions={{
                    color: selected ? '#fff' : '#0f172a',
                    weight: 2,
                    fillColor: w.kind === 'inj' ? '#2563eb' : '#16a34a',
                    fillOpacity: 1,
                  }}
                  eventHandlers={
                    onSelect
                      ? {
                          click: (e) => {
                            L.DomEvent.stopPropagation(e);
                            onSelect(w.id);
                          },
                        }
                      : undefined
                  }
                />
                <Marker
                  position={w.ll}
                  icon={wellLabelIcon(w.label, w.kind)}
                  interactive={false}
                  zIndexOffset={500}
                />
              </React.Fragment>
            );
          })}
        </MapContainer>
      ) : (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'grey.500',
            bgcolor: 'grey.100',
          }}
        >
          No contour / wells for map
        </Typography>
      )}
    </Box>
  );
};
